import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, portalApi } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PENDING_2FA_KEY, TOKEN_STORAGE_KEY } from '../../config';
import { useAuth } from '../../context/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const res = await portalApi.login(normalizedEmail, password);
      if (!res.ok) throw new Error(res.error ?? 'Login failed');
      if (res.requires_2fa && res.pending_token) {
        localStorage.setItem(PENDING_2FA_KEY, res.pending_token);
        navigate('/verify-2fa');
        return;
      }
      if (res.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
        setToken(res.token);
        navigate('/');
      }
    } catch (err) {
      const msg = getApiErrorMessage(err);
      const hint =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { hint?: string } } }).response?.data?.hint === 'string'
          ? (err as { response: { data: { hint: string } } }).response.data.hint
          : null;
      setError(hint ? `${msg} ${hint}` : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-onyx-bg to-onyx-surface">
      <form onSubmit={onSubmit} className="glass-panel w-full max-w-md p-8 space-y-5">
        <div className="text-center mb-2">
          <p className="font-display text-3xl text-onyx-gold">Onyx Pay</p>
          <p className="text-sm text-onyx-muted mt-1">Merchant sign in</p>
        </div>
        {error ? <p className="text-sm text-onyx-danger">{error}</p> : null}
        <label className="block text-sm text-onyx-muted">
          Email
          <Input type="email" className="mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="block text-sm text-onyx-muted">
          Password
          <Input
            type="password"
            className="mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-center text-sm text-onyx-muted">
          New merchant?{' '}
          <Link to="/register" className="text-onyx-gold hover:underline">
            Register
          </Link>
        </p>
      </form>
    </div>
  );
}
